import { useTicketLifecycle } from 'features/libra/lifecycle/TicketLifecycleContext'
import {
  FieldValue,
  FieldValues,
  FieldValueSource,
  TicketInputProvenance,
} from 'features/libra/ontology/ontologyFieldTypes'
import { getInactiveStepIds } from 'features/libra/workflow/inactiveSteps'
import { resolveStepBranchTarget } from 'features/libra/workflow/stepBranching'
import { useWorkflow } from 'features/libra/WorkflowContext'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { RolodexEntryTagsType } from 'types'

import { buildStepMovePatch } from './deriveTicketPatch'
import { applyOriginOption, FieldOriginOption } from './fieldOrigin'
import { buildFieldValuesPatch } from './fieldValuesPatch'
import { LibraTicket } from './libraTicketsApi'
import { useTicketWriter } from './TicketWriterContext'

export interface TicketAccessContextType {
  /**
   * Moves the ticket to the next active step. When `expectedStepId` is given
   * and the ticket has since moved off that step, the advance is a no-op.
   */
  advanceTicketState: (expectedStepId?: string) => Promise<void>
  /**
   * Raw ticket-root `fieldValues`. Typed as flat `FieldValues` so non-FFS
   * consumers see an unchanged shape, but the runtime object MAY carry a
   * reserved `components` key holding a `ComponentFieldValuesMap`. FFS-scope
   * code that needs to reach into that subtree goes through
   * `useScopedFieldAccess({ kind: 'component', ... })` (or the
   * `getComponentSubtree` helper for pure code); ticket-scope consumers can
   * route through `useScopedFieldAccess({ kind: 'ticket' })` for a slice with
   * `components` stripped.
   */
  fieldValues: FieldValues
  setFieldValue: (
    fieldId: string,
    value: FieldValue,
    option?: FieldOriginOption
  ) => Promise<void>
  /**
   * Ticket-scope writer. Merges the provided partial into the top-level slice
   * and **preserves** the reserved `components` subtree unconditionally — a
   * caller accidentally passing a `components` key cannot stomp per-component
   * state. Resolves `true` only when a write was issued (`false` when
   * read-only or the write was skipped).
   */
  setFieldValues: (
    values: FieldValues,
    option?: FieldOriginOption
  ) => Promise<boolean>
  /**
   * Annotation-only provenance overlay from the ticket (`fieldPath -> source`).
   * Read alongside a value resolved from the snapshot / ffs-config to render
   * its citation. See `stepComponents/ffs/inputProvenance.ts`.
   */
  inputProvenance: TicketInputProvenance
  /**
   * Merge many `fieldPath -> source` annotations in ONE ticket patch (one PUT),
   * so a multi-field save can't partially succeed and leave mixed
   * persisted/lost citations. Resolves once the write has persisted and rejects
   * on a hard failure, so callers can await it and branch on the outcome.
   * A no-op (resolves immediately) when `entries` is empty. Never writes the
   * values themselves — those live on the snapshot / ffs-config.
   */
  setInputSources: (entries: Record<string, FieldValueSource>) => Promise<void>
  isLoading: boolean
  readOnly: boolean
  ticketTags: RolodexEntryTagsType
  ticketId: string
  ticketVersion: number

  ticketStatus: string
  /** Who moved the ticket into its current terminal stage, and when. */
  terminalTransitionBy: string | null
  terminalTransitionAt: string | null
  /** Who moved the ticket from in_progress to pending_review, and when. */
  submittedForReviewBy: string | null
  submittedForReviewAt: string | null
  isFullyLocked: boolean

  canEditFields: boolean
  canComment: boolean
  canCreateVersion: boolean
}

const TicketAccessContext = createContext<TicketAccessContextType | null>(null)

export const TicketAccessProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  isLoading: boolean
}> = ({ children, ticket, isLoading }) => {
  const workflow = useWorkflow()
  const { lifecycleConfig, isLocked, isTerminal } = useTicketLifecycle()
  const { enqueue, isSaving } = useTicketWriter()

  // TODO: Restore group permission checks when role-based editing is re-enabled.
  const canEditFields = true
  const canComment = true
  const canCreateVersion = true

  const readOnly = useMemo(() => {
    if (lifecycleConfig) {
      return isLocked || isTerminal || !canEditFields
    }
    return ticket.workflowComplete || !canEditFields
  }, [
    lifecycleConfig,
    isLocked,
    isTerminal,
    canEditFields,
    ticket.workflowComplete,
  ])

  const advanceTicketState = useCallback(
    async (expectedStepId?: string) => {
      if (readOnly) {
        console.warn(`Ticket ${ticket.id} is read-only`)
        return
      }

      await enqueue((latest) => {
        if (expectedStepId !== undefined && latest.stepId !== expectedStepId) {
          return null
        }
        const currentStepIndex = workflow.stepIndices.get(latest.stepId) ?? -1
        const currentStep = workflow.steps[currentStepIndex]
        let nextStep = workflow.steps[currentStepIndex + 1]
        const branchTarget = currentStep?.branch
          ? resolveStepBranchTarget(
              currentStep.branch,
              latest.fieldValues,
              nextStep ? { goToStepId: nextStep.id } : undefined
            )
          : undefined
        if (branchTarget) {
          const targetIndex = workflow.stepIndices.get(branchTarget.goToStepId)
          if (targetIndex !== undefined) {
            nextStep = workflow.steps[targetIndex]
          }
        }

        const inactive = getInactiveStepIds(workflow, latest.fieldValues)
        while (nextStep && inactive.has(nextStep.id)) {
          const nextIndex = workflow.stepIndices.get(nextStep.id) ?? -1
          nextStep = workflow.steps[nextIndex + 1]
        }

        if (!nextStep) return null
        return buildStepMovePatch(
          lifecycleConfig,
          latest.ticketStatus,
          nextStep.id
        )
      })
    },
    [enqueue, readOnly, ticket.id, workflow, lifecycleConfig]
  )

  const setFieldValues = useCallback(
    async (values: FieldValues, option?: FieldOriginOption) => {
      if (readOnly) {
        console.warn(`Ticket ${ticket.id} is read-only`)
        return false
      }
      const applied = await enqueue(
        (latest) => ({
          // Reserved-subtree preservation lives in ONE place; see
          // `buildFieldValuesPatch` for the merge rules.
          fieldValues: buildFieldValuesPatch(
            latest.fieldValues,
            { kind: 'ticket' },
            applyOriginOption(values, option)
          ),
        }),
        { applyInteractionSideEffects: true }
      )
      return applied !== null
    },
    [enqueue, readOnly, ticket.id]
  )

  const setFieldValue = useCallback(
    async (fieldId: string, value: FieldValue, option?: FieldOriginOption) => {
      await setFieldValues({ [fieldId]: value }, option)
    },
    [setFieldValues]
  )

  const setInputSources = useCallback(
    async (entries: Record<string, FieldValueSource>) => {
      if (readOnly) {
        console.warn(`Ticket ${ticket.id} is read-only`)
        return
      }
      if (Object.keys(entries).length === 0) return
      // Merge every annotation into a SINGLE enqueue → one Rolodex PUT, so the
      // batch is all-or-nothing (the FIFO writer rejects the whole item on a
      // hard failure) rather than N independent writes that can partially land.
      await enqueue(
        (latest) => ({
          inputProvenance: { ...latest.inputProvenance, ...entries },
        }),
        { applyInteractionSideEffects: true }
      )
    },
    [enqueue, readOnly, ticket.id]
  )

  const {
    fieldValues,
    inputProvenance,
    tags: ticketTags,
    id: ticketId,
    version: ticketVersion,
  } = ticket
  const isFullyLocked = isTerminal || (isLocked && !!lifecycleConfig)

  const value = useMemo<TicketAccessContextType>(
    () => ({
      advanceTicketState,
      fieldValues,
      setFieldValue,
      setFieldValues,
      inputProvenance,
      setInputSources,
      isLoading: isLoading || isSaving,
      readOnly,
      ticketTags,
      ticketId,
      ticketVersion,

      ticketStatus: ticket.ticketStatus,
      terminalTransitionBy: ticket.terminalTransitionBy,
      terminalTransitionAt: ticket.terminalTransitionAt,
      submittedForReviewBy: ticket.submittedForReviewBy,
      submittedForReviewAt: ticket.submittedForReviewAt,
      isFullyLocked,

      canEditFields,
      canComment,
      canCreateVersion,
    }),
    [
      advanceTicketState,
      fieldValues,
      setFieldValue,
      setFieldValues,
      inputProvenance,
      setInputSources,
      isLoading,
      isSaving,
      readOnly,
      ticketTags,
      ticketId,
      ticketVersion,
      ticket.ticketStatus,
      ticket.terminalTransitionBy,
      ticket.terminalTransitionAt,
      ticket.submittedForReviewBy,
      ticket.submittedForReviewAt,
      isFullyLocked,
      canEditFields,
      canComment,
      canCreateVersion,
    ]
  )

  return (
    <TicketAccessContext.Provider value={value}>
      {children}
    </TicketAccessContext.Provider>
  )
}

export const useTicketAccess = (): TicketAccessContextType => {
  const ctx = useContext(TicketAccessContext)
  if (!ctx) {
    throw new Error(
      'useTicketAccess must be used within a TicketAccessProvider'
    )
  }
  return ctx
}
